import { useCallback } from 'react'

import { usePlatform } from '~/shared/platform'
import { createLocalStorageHook } from '~/shared/lib/persist'

/**
 * Помним, что уже спрашивали. Отказ — это ответ, а не повод переспросить на следующем запуске:
 * повторный попап на том же месте читается как «пока не согласишься, не отстану».
 */
const useHasAsked = createLocalStorageHook('bot_write_access_asked', {
  type: 'boolean',
  defaultValue: false,
})

/**
 * Разрешение боту писать в личку — и, следом за ним, сам чат с ботом.
 *
 * ЗАЧЕМ. Мини-аппу открывают по ссылке `t.me/<bot>?startapp=inv_…`, и пришедший так человек
 * с ботом никогда не переписывался. Для него это тупик: чата с ботом в списке нет, вернуться
 * в приложение неоткуда, писать ему бот не имеет права — то есть ни лучиков, ни маячка, ни
 * предсказания дня он не получит и о том, что они есть, не узнает. Ровно это основатель и
 * увидел на живых аккаунтах.
 *
 * ЧТО ДЕЛАЕТ TELEGRAM. `requestWriteAccess` (Bot API 6.9) показывает нативный попап и снимает
 * запрет на отправку. Сам по себе чат в списке он НЕ создаёт — только разрешает писать.
 * Зато бот получает служебный апдейт `write_access_allowed`, и первое же его сообщение
 * (`bot/handlers.ts`, то же знакомство, что и на `/start`) создаёт диалог. Поэтому шага два,
 * и второй — серверный: без него разрешение молча лежало бы без применения.
 *
 * КОГДА СПРАШИВАЕМ. Сразу на входе — навязчиво: человек ещё не знает, кто такая Луня и зачем
 * ей его писать, и попап получает отказ, который потом не переспросишь. Поэтому спрашиваем в
 * конце онбординга, когда он уже увидел своё небо и понял, что уведомления — это лучики от
 * своих (`OnboardingPage.finishOnboarding`).
 *
 * ЧТО ПРИ ОТКАЗЕ. Ничего: приложение работает целиком, просто пуши не летят. Второй раз не
 * спрашиваем. Клиенты старее 6.9 и браузер отвечают `false` — там метода нет вовсе.
 */
export function useBotChatAccess(): { request: () => Promise<void> } {
  const platform = usePlatform()
  const [hasAsked, setHasAsked] = useHasAsked()

  const request = useCallback(async () => {
    // Разрешение уже есть — попап Telegram не покажет вовсе, но и звать его незачем.
    if (hasAsked || platform.user?.allowsWriteToPm) return

    // Метку ставим ДО запроса: если человек закроет мини-аппу поверх попапа, колбэк может
    // и не прийти, а спрашивать снова на следующем запуске мы не хотим.
    setHasAsked(true)
    await platform.requestWriteAccess()
  }, [hasAsked, platform, setHasAsked])

  return { request }
}
